import React from 'react';
import { motion } from 'framer-motion';
import { pagePaths } from '../utils/sitePaths';

const Home = () => {
  const navigateToPage = (page) => {
    // This will be handled by the parent App component
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[82vh] md:h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/hero-image.jpg')",
            backgroundPosition: '50% 30%',
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/55" />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl sm:text-5xl md:text-7xl font-playfair font-bold mb-5 leading-tight"
            >
              One Partner for Memorable Events
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base sm:text-xl md:text-2xl mb-6 sm:mb-8 font-light text-white/90 leading-relaxed"
            >
              Ubuntu Events Management is a full-service event solutions company offering catering, decor, sound hire, equipment hire, team building, and venue support across Gauteng.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 justify-center"
            >
              <button
                onClick={() => navigateToPage('menu')}
                className="btn-primary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                View Catering Menu
              </button>
              <button
                onClick={() => navigateToPage('spitbraai')}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                🔥 Spit Braai Hire
              </button>
              <button
                onClick={() => navigateToPage('decor')}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                Explore Decor Gallery
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:gap-4 justify-center mt-4"
            >
              <button
                onClick={() => navigateToPage('estimate')}
                className="btn-primary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                💰 Get Estimate
              </button>
              <button
                onClick={() => navigateToPage('teambuilding')}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                🤝 Team Building
              </button>
            </motion.div>
            
            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:gap-4 justify-center mt-4 sm:mt-6"
            >
              <button
                onClick={() => navigateToPage('soundhire')}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                🎧 Sound Hire
              </button>
              <button
                onClick={() => navigateToPage('venuesupport')}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap"
              >
                🏟️ Venue Support
              </button>
              <a
                href={pagePaths.blog}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToPage('blog');
                }}
                className="btn-secondary text-sm min-[380px]:text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 whitespace-nowrap inline-flex items-center justify-center"
              >
                📝 Event Planning Blog
              </a>
            </motion.div>
      </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              About UBUNTU EVENTS MANAGEMENT
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                Ubuntu Events Management is a full-service event solutions company offering catering, decor, sound hire, equipment hire, team building, and venue support across Gauteng.
                We help clients in Pretoria, Centurion, Midrand, Johannesburg, and wider Gauteng bring their events to life with style, energy, and convenience - whether
                it’s an intimate indoor gathering, a large outdoor celebration, a corporate function,
                or a team-building experience. From planning to setup, we make events simpler, smoother,
                and more memorable.
              </p>
              <p className="text-base text-gold font-semibold mb-6">Limited availability. Weekend bookings fill fast.</p>
              
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">🍽️</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Fine Catering
                  </h3>
                  <p className="text-gray-600">
                    Delicious, fresh meals prepared with love and attention to detail
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">🎨</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Elegant Decor
                  </h3>
                  <p className="text-gray-600">
                    Beautiful decorations that transform any space into a magical setting
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="text-4xl mb-4">✨</div>
                  <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-3">
                    Full Coordination
                  </h3>
                  <p className="text-gray-600">
                    Complete event management from planning to execution
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              Our Services
            </h2>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              From food and decor to sound, smoke effects, venue support, and team experiences, we provide reliable service and full-service event solutions in Pretoria and across Gauteng.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mt-12">
            {[
              {
                key: 'catering',
                icon: '🍽️',
                title: 'Catering',
                image:
                  'https://images.unsplash.com/photo-1541544181074-e6d3c9e7e4f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'decor',
                icon: '🎨',
                title: 'Decor',
                image:
                  'https://images.unsplash.com/photo-1523438097201-512ae64f41d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'spitbraai',
                icon: '🔥',
                title: 'Spit Braai',
                image:
                  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'teambuilding',
                icon: '🤝',
                title: 'Team Building',
                image:
                  'https://images.unsplash.com/photo-1521737604893-d14ccf9a7b3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'soundhire',
                icon: '🎧',
                title: 'Sound Hire',
                image:
                  'https://images.unsplash.com/photo-1524368535928-7b4a17b4a39e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'smoke',
                icon: '💨',
                title: 'Smoke Effects',
                image:
                  'https://images.unsplash.com/photo-1552552770-7f8d88d2fa2a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
              {
                key: 'venuesupport',
                icon: '🏟️',
                title: 'Venue Support',
                image:
                  'https://images.unsplash.com/photo-1528909514045-2fa4ac7a08ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
              },
            ].map((service, idx) => (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl cursor-pointer flex flex-col"
                onClick={() => {
                  if (service.key === 'soundhire') return navigateToPage('soundhire');
                  if (service.key === 'venuesupport') return navigateToPage('venuesupport');
                  if (service.key === 'spitbraai') return navigateToPage('spitbraai');
                  if (service.key === 'decor') return navigateToPage('decor');
                  if (service.key === 'teambuilding') return navigateToPage('teambuilding');
                  if (service.key === 'catering') return navigateToPage('menu');
                  // smoke effects are handled via Sound Hire
                  if (service.key === 'smoke') return navigateToPage('soundhire');
                }}
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-xl mb-5">
                  <img
                    src={service.image}
                    alt={`${service.title} event services Gauteng in Pretoria and Johannesburg`}
                    className="w-full h-36 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-3xl sm:text-4xl text-gold">{service.icon}</div>
                  <h3 className="text-xl sm:text-2xl font-playfair font-semibold text-gray-800">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mt-4 leading-relaxed">
                  Premium support designed to make your event feel effortless.
                </p>
              </motion.div>
            ))}
          </div>

          {/* Featured service */}
          <div className="mt-16 card bg-gradient-to-r from-gold/10 to-olive/10 border-2 border-gold/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 sm:gap-8">
              <div className="max-w-2xl">
                <h3 className="text-3xl md:text-4xl font-playfair font-bold text-gray-800 mb-3">
                  Sound, Smoke &amp; Venue Support
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Whether you need clear sound for speeches, smoke effects for an
                  unforgettable entrance, or help finding the perfect venue, UBUNTU
                  EVENTS MANAGEMENT can package it into one seamless event solution.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:gap-4">
                <button
                  onClick={() => navigateToPage('soundhire')}
                  className="btn-primary text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4"
                >
                  View Sound Hire
                </button>
                <button
                  onClick={() => navigateToPage('estimate')}
                  className="btn-secondary text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4"
                >
                  Get Estimate
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spit Braai Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              🔥 Hire a Spit Braai for Your Next Event
            </h2>
            
            <div className="max-w-4xl mx-auto mb-12">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Make your event unforgettable with our delicious spit braai service — 
                available for hire with or without catering.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="order-2 md:order-1"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-lg shadow-lg">
                  <img
                    src="/spit-braai-preview.jpg"
                    alt="Spit braai hire Gauteng service setup"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="order-1 md:order-2 text-left"
              >
                <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-4">
                  Professional Spit Braai Service
                </h3>
                <p className="text-gray-600 mb-6">
                  From intimate gatherings to large celebrations, our spit braai service brings 
                  authentic South African flavor to your event. Choose from our Standard, Full Service, 
                  or Premium packages to suit your needs.
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center">
                    <span className="text-gold mr-3">✓</span>
                    <span className="text-gray-700">Professional setup and operation</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gold mr-3">✓</span>
                    <span className="text-gray-700">Quality equipment and charcoal</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gold mr-3">✓</span>
                    <span className="text-gray-700">Flexible packages for any budget</span>
                  </div>
                </div>
                <button
                  onClick={() => navigateToPage('spitbraai')}
                  className="btn-primary text-lg px-8 py-4"
                >
                  Learn More
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gold/10 to-olive/10">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-gray-800 mb-6">
              Ready to create your perfect event?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Let us help you turn your vision into reality with our professional services
            </p>
            <button
              onClick={() => navigateToPage('contact')}
              className="btn-primary text-lg px-8 py-4"
            >
              Get In Touch Today
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
